/** Small shared pieces of the design system: pills, bars, the tree, tallies. */

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import type { Status } from '@svcdue/core';

/** Status as the UI knows it — the engine's three, plus "needs a human". */
export type UiStatus = Status | 'inspect';

export const Eyebrow = ({ children }: { children: ReactNode }) => <div className="eyebrow">{children}</div>;

const STATUS_TEXT: Record<UiStatus, string> = {
  good: 'Good',
  due: 'Due soon',
  over: 'Overdue',
  inspect: 'Inspect',
};

export function Pill({ s }: { s: UiStatus }) {
  return (
    <span className={'pill pill-' + s}>
      <span className="pill-dot" />
      {STATUS_TEXT[s]}
    </span>
  );
}

export function Trigger({ children }: { children: ReactNode }) {
  return <span className="trig">{children}</span>;
}

export function Bar({ pct, s }: { pct: number; s: UiStatus }) {
  return (
    <div className="bar">
      <div className={'bar-fill bar-' + s} style={{ width: Math.min(100, pct) + '%' }} />
    </div>
  );
}

/** The signature: a drag-strip Christmas tree, used as the status legend. */
export function Tree({ animate }: { animate?: boolean }) {
  const [lit, setLit] = useState(!animate);

  useEffect(() => {
    if (!animate) return;
    const t = setTimeout(() => setLit(true), 150);
    return () => clearTimeout(t);
  }, [animate]);

  const bulb = (cls: string, i: number) => (
    <div key={cls + i} className={'bulb ' + cls + (lit ? ' lit' : '')} style={{ transitionDelay: i * 90 + 'ms' }} />
  );

  return (
    <div className="tree">
      <div className="tree-pole" />
      <div className="tree-bulbs">
        {bulb('b-stage', 0)}
        {bulb('b-stage', 1)}
        <div className="tree-gap" />
        {bulb('b-amber', 2)}
        {bulb('b-amber', 3)}
        {bulb('b-amber', 4)}
        {bulb('b-green', 5)}
        {bulb('b-red', 6)}
      </div>
    </div>
  );
}

/**
 * The tree as a loading indicator — a staging countdown that loops.
 *
 * Instead of a generic spinner, the three ambers light in sequence and the
 * green flashes, over and over, the way a real drag-strip tree counts a car
 * down to launch. It is the app's signature shape doing double duty, so a wait
 * still feels like the product rather than a blank pause. The animation is all
 * CSS (`.tree-run`), and collapses to a static lit tree under reduced-motion.
 */
export function LoadingTree() {
  return (
    <div className="tree tree-run" aria-hidden="true">
      <div className="tree-pole" />
      <div className="tree-bulbs">
        <div className="bulb b-stage" />
        <div className="bulb b-stage" />
        <div className="tree-gap" />
        <div className="bulb b-amber run run-0" />
        <div className="bulb b-amber run run-1" />
        <div className="bulb b-amber run run-2" />
        <div className="bulb b-green run run-3" />
        <div className="bulb b-red" />
      </div>
    </div>
  );
}

export function Tally({ t }: { t: { good: number; due: number; over: number } }) {
  return (
    <div className="tally">
      <span className="tally-i">
        <i className="dot d-green" />
        {t.good} good
      </span>
      <span className="tally-i">
        <i className="dot d-amber" />
        {t.due} due
      </span>
      <span className="tally-i">
        <i className="dot d-red" />
        {t.over} overdue
      </span>
    </div>
  );
}
